<script setup>
import { ref, computed, watch } from 'vue'
import { useTranslaterStore } from '@/stores/translater'
import { useEcusFirmwareStore } from '@/stores/ecus-firmwares'
import Modal from '@/components/UTAModal.vue'

const props = defineProps({
    uuid: {
        type: String,
        required: true
    }
})

const translaterStore = useTranslaterStore()
const ecusFirmwareStore = useEcusFirmwareStore()

const getTranslate      = computed(() => translaterStore.getTranslate)
const firmwares         = computed(() => ecusFirmwareStore.firmwares)
const firmwaresProcess  = computed(() => ecusFirmwareStore.firmwaresProcess)
const firmwaresMsg      = computed(() => ecusFirmwareStore.firmwaresMsg)
const firmwareCreateMsg = computed(() => ecusFirmwareStore.firmwareCreateMsg)
const firmwareDeleteMsg = computed(() => ecusFirmwareStore.firmwareDeleteMsg)

const showDeleteFirmware = ref(false)
const deleteFirmwareName = ref('')

const formErrors = ref([])
const formInvalids = ref({})

ecusFirmwareStore.readFirmwares({ uuid_car_array: props.uuid })

function submitForm($event) {

    formErrors.value     = []
    formInvalids.value   = {}

    const fields    = $event.target.elements
    const file      = fields.upFile.files[0]

    if(file) {

        // TODO: Проверить актуальность [максимальный вес прошивки - 16777216 бит / 2048 КБ / 2 МБ]
        if(file.size < 16777216) {

            if(file.size == 0) {

                formInvalids.value.upFile = true
                formErrors.value.push(getTranslate.value.ECU_FIRMWARE_FILE_EMPTY)
            }

        } else {

            formInvalids.value.upFile = true
            formErrors.value.push(getTranslate.value.ECU_FIRMWARE_FILE_LARGE)
        }

    } else {

        formInvalids.value.upFile = true
        formErrors.value.push(getTranslate.value.ECU_FIRMWARE_FILE_ERROR)
    }

    if(!formErrors.value.length) {

        const data = { data: new FormData($event.target), car: props.uuid }
        ecusFirmwareStore.createFirmware(data)
    }
}

watch(firmwareDeleteMsg, msg => {
    if(msg && msg.success) {
        showDeleteFirmware.value = false
        ecusFirmwareStore.readFirmwares({ uuid_car_array: props.uuid })
    }
})

watch(firmwareCreateMsg, msg => {
    if(msg && msg.success) {
        ecusFirmwareStore.readFirmwares({ uuid_car_array: props.uuid })
    }
})


</script>

<template>

    <div class="uta-gnss-firmwares">

        <div class="mb-4 uta-alert uta-alert--success" v-if="'success' in firmwareDeleteMsg && firmwareDeleteMsg.success">
            <span class="material-icons uta-alert__icon">
                error_outline
            </span>
            <div class="uta-alert__text">
                {{ getTranslate[firmwareDeleteMsg.text] || firmwareDeleteMsg.text }}
            </div>
            <button type="button" class="uta-alert__close" data-bs-dismiss="alert" aria-label="Close" @click="ecusFirmwareStore.setDeleteFirmwareMsg({})">
                <span class="material-icons">
                    close
                </span>
            </button>
        </div>

        <div class="mb-4 uta-alert uta-alert--danger" v-if="'success' in firmwaresMsg && !firmwaresMsg.success">
            <span class="material-icons uta-alert__icon">
                error_outline
            </span>
            <div class="uta-alert__text">
                {{ getTranslate[firmwaresMsg.text] || firmwaresMsg.text }}
            </div>
        </div>

        <div class="container-fluid">

            <div class="loader loader--component-lg" v-if="firmwaresProcess && !firmwares.array">
                <div class="spinner-border text-secondary" role="status">
                    <span class="visually-hidden">Loading...</span>
                </div>
            </div>

            <div class="my-4 uta-list uta-list--sm uta-gnss-firmwares__list" v-if="firmwares.array">

                <div class="d-none d-md-block uta-list__title uta-gnss-firmwares__list-header">
                    <div class="row">
                        <div class="col-10 col-lg-11">
                            <div class="row">
                                <div class="col-4 uta-gnss-firmwares__list-header-file">{{ getTranslate.ECU_FIRMWARE_FILE }}</div>
                                <div class="col-4 uta-gnss-firmwares__list-header-type">{{ getTranslate.ECU_FIRMWARE_TYPE }}</div>
                                <div class="col-4 uta-gnss-firmwares__list-header-status">{{ getTranslate.ECU_FIRMWARE_STATUS }}</div>
                            </div>
                        </div>
                        <div class="col-2 col-lg-1"></div>
                    </div>
                </div>

                <div class="uta-uta-list__content uta-gnss-firmwares__list-items">
                    <div class="uta-list__item uta-gnss-firmwares__firmware" v-for="(firmware, index) of firmwares.array" :key="index">
                        <div class="row">
                            <div class="col-10 col-lg-11">
                                <div class="row">
                                    <div class="col-12 col-md-4 uta-gnss-firmwares__firmware-file">
                                        <span class="d-inline d-md-none uta-list__item-mobile-title">{{ getTranslate.ECU_FIRMWARE_FILE }}: </span>
                                        {{ firmware.name }}
                                    </div>
                                    <div class="col-12 col-md-4 uta-gnss-firmwares__firmware-type">
                                        <span class="d-inline d-md-none uta-list__item-mobile-title">{{ getTranslate.ECU_FIRMWARE_TYPE }}: </span>
                                        {{ getTranslate['ECU_FIRMWARE_TYPE_' + firmware.for.toUpperCase()] }}
                                    </div>
                                    <div class="col-12 col-md-4 uta-gnss-firmwares__firmware-status">
                                        <span class="d-inline d-md-none uta-list__item-mobile-title">{{ getTranslate.ECU_FIRMWARE_STATUS }}: </span>
                                        {{ getTranslate['ECU_FIRMWARE_STATUS_' + firmware.status.toUpperCase()] || firmware.status || '&mdash;' }}
                                        <span class="uta-gnss-firmwares__firmware-status-error" v-if="firmware.errorMessage">
                                            [{{ firmware.errorMessage }}]
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div class="col-2 col-lg-1 text-end">
                                <span 
                                    class="uta-list__item-icon material-icons" 
                                    @click="
                                        showDeleteFirmware = true, 
                                        deleteFirmwareName = firmware.name
                                    "
                                >
                                    delete
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

        </div>

        <div class="my-4 uta-gnss-firmwares__upload">

            <h3 class="mb-4">{{ getTranslate.ECU_FIRMWARE_UPLOAD }}</h3>

            <form class="uta-gnss-firmwares__upload-form" @submit.prevent="submitForm($event)" enctype="multipart/form-data">

                <div class="mb-4 uta-alert uta-alert--danger" v-if="Object.keys(formInvalids).length">
                    <span class="material-icons uta-alert__icon">check_circle_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate.EFormFields }}:
                        <span class="alert__text-item" v-for="(error, index) in formErrors" :key="error">
                            {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                        </span>
                    </div>
                    <button type="button" class="uta-alert__close" @click="formErrors = [], formInvalids = {}">
                        <span class="material-icons">close</span>
                    </button>
                </div>

                <div 
                    v-if="'success' in firmwareCreateMsg"
                    class="mb-4 uta-alert"
                    :class="{ 
                        'uta-alert--danger' : !firmwareCreateMsg.success,
                        'uta-alert--success' : firmwareCreateMsg.success
                    }"
                >
                    <span class="material-icons uta-alert__icon">
                        error_outline
                    </span>
                    <div class="uta-alert__text">
                        {{ getTranslate[firmwareCreateMsg.text] }}
                    </div>
                    <button type="button" class="uta-alert__close" @click="ecusFirmwareStore.setCreateFirmwareMsg({})">
                        <span class="material-icons">
                            close
                        </span>
                    </button>
                </div>

                <div class="row">
                    <div class="col-12 col-md-6">
                        <div class="form-group uta-gnss-firmwares__upload-form-field">
                            <label for="ADTCarGNSSFirmwaresFormType">{{ getTranslate.ECU_FIRMWARE_TYPE }}:</label>
                            <select id="ADTCarGNSSFirmwaresFormType" name="for" class="custom-select uta-input uta-input--block form-group__select uta-gnss-firmwares__upload-form-select">
                                <option value="All">{{ getTranslate.ECU_FIRMWARE_TYPE_ALL }}</option>
                                <option value="ALL_EGTS">{{ getTranslate.ECU_FIRMWARE_TYPE_ALL_EGTS }}</option>
                                <option value="ALL_RLP">{{ getTranslate.ECU_FIRMWARE_TYPE_ALL_RLP }}</option>
                            </select>
                        </div>
                    </div>
                    <div class="col-12 col-md-6">
                        <div class="form-group uta-gnss-firmwares__upload-form-field">
                            <label for="upFile">{{ getTranslate.ECU_FIRMWARE_FILE }}:</label>
                            <input 
                                type="file" 
                                class="uta-input uta-input--block form-group__input uta-gnss-firmwares__upload-form-input"
                                id="upFile"
                                name="upFile"
                                @input="delete formInvalids.upFile"
                                :class="{ 'uta-input--invalid': formInvalids.upFile }" 
                            >
                        </div>
                    </div>
                </div>

                <div class="row">
                    <div class="col-24 col-sm-8 col-md-4">
                        <button class="uta-btn uta-btn--block uta-btn--main uta-gnss-firmwares__upload-form-submit" type="submit">
                            {{ getTranslate.BTN_UPLOAD }}
                        </button>
                    </div>
                </div>

                <input type="hidden" id="uuid" name="car" :value="props.uuid">

            </form>

        </div>

        <transition name="bounce">

            <Modal 
                v-if="showDeleteFirmware"
                :header="getTranslate.ECU_GNSS_FIRMWARES_DELETE"
                size="md"
                @closeModal="
                    showDeleteFirmware = false, 
                    ecusFirmwareStore.setDeleteFirmwareMsg({})
                "
            >
                <template v-slot:body>
                    <div 
                        class="uta-alert uta-alert--danger" 
                        v-if="'success' in firmwareDeleteMsg && !firmwareDeleteMsg.success"
                    >
                        {{ getTranslate[firmwareDeleteMsg.text] }}
                    </div>
                    <div class="uta-alert__text">
                        {{ getTranslate.ECU_GNSS_FIRMWARES_DELETE_DESCR }}
                    </div>
                    <div class="uta-btns">
                        <button class="uta-btn"
                            @click.prevent="
                                showDeleteFirmware = false,
                                deleteFirmwareName = ''
                            "
                        >
                            {{ getTranslate.BTN_CANCEL }}
                        </button>
                        <button class="uta-btn uta-btn--main"
                            @click.prevent="
                                ecusFirmwareStore.deleteFirmware({ uuid: props.uuid, name: deleteFirmwareName })
                            "
                        >
                            {{ getTranslate.BTN_DELETE }}
                        </button>
                    </div>
                </template>
            </Modal>

        </transition>

    </div>

</template>

<style lang="scss">
@import '@/assets/styles/scss/components/car/ecus/gnss-firmwares.scss';
</style>